import React from "react";
import { Award, Users, Globe, Clock, CheckCircle } from "lucide-react";
import { companyInfo } from "../data/mockData";

const AboutPage = () => {
  const stats = [
    { label: "Years of Experience", value: "10+", icon: Clock },
    { label: "Vehicles Traded", value: "5,000+", icon: Award },
    { label: "Happy Clients", value: "2,500+", icon: Users },
    { label: "Countries Served", value: "25+", icon: Globe },
  ];

  const values = [
    {
      title: "Precision",
      description: "Meticulous attention to detail in every aspect of our service, from vehicle selection to final delivery.",
      icon: CheckCircle
    },
    {
      title: "Prestige", 
      description: "Maintaining the highest standards of luxury and quality in all our automotive trading operations.",
      icon: Award
    },
    {
      title: "Performance",
      description: "Delivering exceptional results through efficient processes and cutting-edge logistics solutions.",
      icon: Globe
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-96 bg-gradient-to-r from-black via-gray-900 to-royal-red/20 flex items-center">
        <div className="container mx-auto">
          <div className="animate-fadeInUp">
            <h1 className="text-5xl font-bold text-white mb-4">About AD Motors</h1>
            <p className="text-xl text-gray-200 max-w-3xl">
              Japan's premier automotive import/export company, dedicated to connecting global clients with premium Japanese vehicles.
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeInLeft">
              <h2 className="text-4xl font-bold text-white mb-6">Our Story</h2>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Founded with a vision to bridge the gap between Japan's exceptional automotive market and global enthusiasts, 
                AD Motors (ADモーターズ) has established itself as a trusted name in luxury vehicle trading.
              </p>
              <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                Our deep understanding of both Japanese auction systems and international requirements allows us to 
                provide seamless import/export services that exceed expectations.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                From rare collector cars to modern luxury vehicles, we specialize in sourcing, inspecting, and delivering 
                premium automobiles that represent the pinnacle of Japanese craftsmanship and engineering excellence.
              </p>
            </div>
            <div className="animate-fadeInRight">
              <img
                src="https://images.unsplash.com/photo-1547731269-e4073e054f12?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwYXV0b21vdGl2ZXxlbnwwfHx8fDE3NTQzODM3NDZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Luxury car interior"
                className="rounded-xl shadow-2xl hover-lift"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section-padding bg-black">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Our Achievement</h2>
            <p className="text-gray-300 text-lg">Numbers that reflect our commitment to excellence</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center glass-effect p-8 rounded-xl hover-lift">
                <div className="bg-royal-red p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-gray-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="glass-effect p-8 rounded-xl">
              <h3 className="text-3xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                To provide unparalleled automotive import/export services that connect discerning clients worldwide 
                with Japan's finest vehicles, ensuring authenticity, quality, and exceptional customer experience 
                in every transaction.
              </p>
            </div>
            <div className="glass-effect p-8 rounded-xl">
              <h3 className="text-3xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-gray-300 text-lg leading-relaxed">
                To be the global leader in premium automotive trading, setting new standards for transparency, 
                reliability, and service excellence while fostering long-term relationships built on trust and 
                mutual success.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Company Values */}
      <section className="section-padding bg-black">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">Our Core Values</h2>
            <p className="text-gray-300 text-lg">The principles that guide everything we do</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center glass-effect p-8 rounded-xl hover-lift">
                <div className="bg-royal-red p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
                <p className="text-gray-300 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Information */}
      <section className="section-padding bg-gray-900">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-white mb-8">Company Information</h2>
              <div className="glass-effect p-8 rounded-xl">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="text-white font-semibold mb-2">Company Name</h4>
                    <p className="text-gray-300 mb-4">
                      {companyInfo.nameEn}<br />
                      {companyInfo.nameJp}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Corporate Number</h4>
                    <p className="text-gray-300 mb-4">{companyInfo.corporateNumber}</p>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Postal Code</h4>
                    <p className="text-gray-300 mb-4">{companyInfo.postalCode}</p>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold mb-2">Contact</h4>
                    <p className="text-gray-300 mb-1">{companyInfo.landline}</p>
                    <p className="text-gray-300">{companyInfo.mobile}</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              {/* Google Maps Placeholder */}
              <div className="glass-effect p-4 rounded-xl">
                <h3 className="text-white font-semibold mb-4">Our Location</h3>
                <div className="bg-gray-800 rounded-lg h-64 flex items-center justify-center">
                  <p className="text-gray-400">Google Maps Integration</p>
                  <p className="text-sm text-gray-500 mt-2">Location: {companyInfo.postalCode}, Japan</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;